import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { 
  ArrowLeft, Calendar, Package, DollarSign, FileText, Download, Mail, 
  CheckCircle, Clock, AlertCircle, User, Building, Truck, Receipt,
  MapPin, Phone, CreditCard
} from 'lucide-react';
import { WarehouseInvoice } from '@shared/sdk-v1/models';
import { WarehouseInvoiceApi } from '@shared/sdk-v1/apis';
import { apiConfig } from '../shared/api-config';
import { useSelector } from 'react-redux';
import { getCourier, getCurrency } from '@state/courierSlice';
import { formatCurrency, formatDualCurrency } from '../utils/currencyUtils';
import { feeTypeLabel, displayLineItemDescription } from '../utils/warehouseLineItem';

const InvoiceDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const courier = useSelector(getCourier);

  const [invoice, setInvoice] = useState<WarehouseInvoice | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const currency = useSelector(getCurrency);
  const settings = courier?.courierSetting;
  // Dual JMD<->USD display only applies to JMD/USD couriers (single exchangeRate).
  const showDual =
    settings?.showDualCurrency &&
    settings?.exchangeRate &&
    (currency === 'JMD' || currency === 'USD');

  const formatAmount = (amount: number | undefined | null): string => {
    if (showDual) {
      return formatDualCurrency(amount, !!settings?.useJmd, settings?.exchangeRate || 1);
    }
    return formatCurrency(amount, currency);
  };

  // Load invoice details
  const loadInvoice = async () => {
    if (!id) return;
    
    try {
      setIsLoading(true);
      setError(null);

      const api = new WarehouseInvoiceApi(apiConfig());
      const result = await api.warehouseInvoiceGetInvoice({
        invoiceId: parseInt(id, 10)
      });

      setInvoice(result);
    } catch (err) {
      console.error('Error loading invoice:', err);
      setError('Failed to load invoice details. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadInvoice();
  }, [id]);

  // Get status badge styling
  const getStatusBadge = (status?: string) => {
    const baseClasses = 'inline-flex items-center px-3 py-1 rounded-full text-sm font-medium';
    
    switch (status?.toLowerCase()) {
      case 'paid':
        return `${baseClasses} bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200`;
      case 'sent':
        return `${baseClasses} bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200`;
      case 'pending':
        return `${baseClasses} bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200`;
      case 'pending_verification':
        return `${baseClasses} bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200`;
      case 'overdue':
        return `${baseClasses} bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200`;
      default:
        return `${baseClasses} bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300`;
    }
  };

  // Customer-facing label for the payment-under-review state
  const getStatusLabel = (status?: string) => {
    if (status?.toLowerCase() === 'pending_verification') return 'Payment Under Review';
    return status ? status.charAt(0).toUpperCase() + status.slice(1) : 'Unknown';
  };

  // Get status icon
  const getStatusIcon = (status?: string) => {
    switch (status?.toLowerCase()) {
      case 'paid':
        return <CheckCircle className="w-5 h-5" />;
      case 'sent':
        return <Clock className="w-5 h-5" />;
      case 'pending':
        return <AlertCircle className="w-5 h-5" />;
      case 'pending_verification':
        return <Clock className="w-5 h-5" />;
      case 'overdue':
        return <AlertCircle className="w-5 h-5" />;
      default:
        return <FileText className="w-5 h-5" />;
    }
  };

  if (isLoading) {
    return (
      <div className="flex flex-col justify-center items-center h-96 space-y-4">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <p className="text-gray-600 dark:text-gray-400">Loading invoice details...</p>
      </div>
    );
  }

  if (error || !invoice) {
    return (
      <div className="p-4">
        <div className="bg-red-50 dark:bg-red-900 border border-red-200 dark:border-red-800 rounded-md p-4">
          <div className="flex">
            <AlertCircle className="h-5 w-5 text-red-400" />
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800 dark:text-red-200">Error</h3>
              <p className="text-sm text-red-700 dark:text-red-300 mt-1">
                {error || 'Invoice not found'}
              </p>
              <button
                onClick={() => navigate('/invoices')}
                className="mt-3 text-sm text-red-600 dark:text-red-400 hover:text-red-500 font-medium"
              >
                ← Back to invoices
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/invoices')}
            className="inline-flex items-center text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
          >
            <ArrowLeft className="w-5 h-5 mr-1" />
            Back to Invoices
          </button>
        </div>
      </div>

      {/* Invoice Header */}
      <div className="bg-white dark:bg-gray-800 shadow rounded-lg border border-gray-200 dark:border-gray-700">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
            <div className="flex items-center space-x-4">
              <div className="flex-shrink-0">
                {getStatusIcon(invoice.status)}
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                  Invoice #{invoice.invoiceNumber || 'N/A'}
                </h1>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Manifest: {invoice.manifest?.manifestNumber || 'N/A'}
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <div className="text-right">
                <div className="text-2xl font-bold text-gray-900 dark:text-white">
                  {formatAmount(invoice.grandTotal || 0)}
                </div>
                <div className={getStatusBadge(invoice.status)}>
                  {getStatusLabel(invoice.status)}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Invoice Summary */}
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="flex items-center space-x-3">
              <Package className="w-8 h-8 text-blue-500" />
              <div>
                <div className="text-sm font-medium text-gray-500 dark:text-gray-400">Packages</div>
                <div className="text-lg font-semibold text-gray-900 dark:text-white">
                  {invoice.packageCount || 0}
                </div>
              </div>
            </div>

            <div className="flex items-center space-x-3">
              <Receipt className="w-8 h-8 text-green-500" />
              <div>
                <div className="text-sm font-medium text-gray-500 dark:text-gray-400">Total Weight</div>
                <div className="text-lg font-semibold text-gray-900 dark:text-white">
                  {invoice.totalWeight ? `${invoice.totalWeight.toFixed(1)} lbs` : 'N/A'}
                </div>
              </div>
            </div>

            {invoice.dueDate && (
              <div className="flex items-center space-x-3">
                <Calendar className="w-8 h-8 text-orange-500" />
                <div>
                  <div className="text-sm font-medium text-gray-500 dark:text-gray-400">Due Date</div>
                  <div className="text-lg font-semibold text-gray-900 dark:text-white">
                    {new Date(invoice.dueDate).toLocaleDateString()}
                  </div>
                </div>
              </div>
            )}

            {invoice.paidDate && (
              <div className="flex items-center space-x-3">
                <CheckCircle className="w-8 h-8 text-green-500" />
                <div>
                  <div className="text-sm font-medium text-gray-500 dark:text-gray-400">Paid Date</div>
                  <div className="text-lg font-semibold text-gray-900 dark:text-white">
                    {new Date(invoice.paidDate).toLocaleDateString()}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Line Items */}
      <div className="bg-white dark:bg-gray-800 shadow rounded-lg border border-gray-200 dark:border-gray-700">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Invoice Details</h2>
        </div>
        <div className="p-6">
          {invoice.lineItems && invoice.lineItems.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-900">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Fee Type
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Description
                    </th>
                    <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Quantity
                    </th>
                    <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Unit Price
                    </th>
                    <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Total
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  {invoice.lineItems.map((item, index) => (
                    <tr key={item.id || index}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                        {feeTypeLabel(item.feeType)}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400">
                        {displayLineItemDescription(item) || 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white text-right">
                        {item.quantity || 1}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white text-right">
                        {formatAmount(item.unitPrice)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white text-right">
                        {formatAmount(item.total)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-8">
              <FileText className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">No line items</h3>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                This invoice doesn't have any detailed line items.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Invoice Totals */}
      <div className="bg-white dark:bg-gray-800 shadow rounded-lg border border-gray-200 dark:border-gray-700">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Invoice Summary</h2>
        </div>
        <div className="p-6">
          <div className="space-y-3">
            {/* Fee Breakdown */}
            {invoice.warehousingTotal !== undefined && invoice.warehousingTotal > 0 && (
              <div className="flex justify-between">
                <span className="text-sm text-gray-600 dark:text-gray-400">Warehousing</span>
                <span className="text-sm font-medium text-gray-900 dark:text-white">
                  {formatAmount(invoice.warehousingTotal)}
                </span>
              </div>
            )}
            {invoice.inspectionTotal !== undefined && invoice.inspectionTotal > 0 && (
              <div className="flex justify-between">
                <span className="text-sm text-gray-600 dark:text-gray-400">Inspection</span>
                <span className="text-sm font-medium text-gray-900 dark:text-white">
                  {formatAmount(invoice.inspectionTotal)}
                </span>
              </div>
            )}
            {invoice.descriptionTotal !== undefined && invoice.descriptionTotal > 0 && (
              <div className="flex justify-between">
                <span className="text-sm text-gray-600 dark:text-gray-400">Description</span>
                <span className="text-sm font-medium text-gray-900 dark:text-white">
                  {formatAmount(invoice.descriptionTotal)}
                </span>
              </div>
            )}
            {invoice.shippingBagTotal !== undefined && invoice.shippingBagTotal > 0 && (
              <div className="flex justify-between">
                <span className="text-sm text-gray-600 dark:text-gray-400">Shipping Bag</span>
                <span className="text-sm font-medium text-gray-900 dark:text-white">
                  {formatAmount(invoice.shippingBagTotal)}
                </span>
              </div>
            )}
            {invoice.missingCodeTotal !== undefined && invoice.missingCodeTotal > 0 && (
              <div className="flex justify-between">
                <span className="text-sm text-gray-600 dark:text-gray-400">Missing Codes</span>
                <span className="text-sm font-medium text-gray-900 dark:text-white">
                  {formatAmount(invoice.missingCodeTotal)}
                </span>
              </div>
            )}
            {invoice.customsTotal !== undefined && invoice.customsTotal > 0 && (
              <div className="flex justify-between">
                <span className="text-sm text-gray-600 dark:text-gray-400">Handling</span>
                <span className="text-sm font-medium text-gray-900 dark:text-white">
                  {formatAmount(invoice.customsTotal)}
                </span>
              </div>
            )}
            {invoice.freightTotal !== undefined && invoice.freightTotal > 0 && (
              <div className="flex justify-between">
                <span className="text-sm text-gray-600 dark:text-gray-400">Freight</span>
                <span className="text-sm font-medium text-gray-900 dark:text-white">
                  {formatAmount(invoice.freightTotal)}
                </span>
              </div>
            )}
            {invoice.seaFreightTotal !== undefined && invoice.seaFreightTotal > 0 && (
              <div className="flex justify-between">
                <span className="text-sm text-gray-600 dark:text-gray-400">Sea Freight</span>
                <span className="text-sm font-medium text-gray-900 dark:text-white">
                  {formatAmount(invoice.seaFreightTotal)}
                </span>
              </div>
            )}
            {invoice.transportationTotal !== undefined && invoice.transportationTotal > 0 && (
              <div className="flex justify-between">
                <span className="text-sm text-gray-600 dark:text-gray-400">Transportation</span>
                <span className="text-sm font-medium text-gray-900 dark:text-white">
                  {formatAmount(invoice.transportationTotal)}
                </span>
              </div>
            )}
            {invoice.otherTotal !== undefined && invoice.otherTotal > 0 && (
              <div className="flex justify-between">
                <span className="text-sm text-gray-600 dark:text-gray-400">Other</span>
                <span className="text-sm font-medium text-gray-900 dark:text-white">
                  {formatAmount(invoice.otherTotal)}
                </span>
              </div>
            )}
            {invoice.peakSurchargeTotal !== undefined && invoice.peakSurchargeTotal > 0 && (
              <div className="flex justify-between">
                <span className="text-sm text-gray-600 dark:text-gray-400">Peak Season Surcharge</span>
                <span className="text-sm font-medium text-gray-900 dark:text-white">
                  {formatAmount(invoice.peakSurchargeTotal)}
                </span>
              </div>
            )}

            <div className="border-t border-gray-200 dark:border-gray-700 pt-3">
              <div className="flex justify-between">
                <span className="text-sm text-gray-600 dark:text-gray-400">Subtotal</span>
                <span className="text-sm font-medium text-gray-900 dark:text-white">
                  {formatAmount(invoice.subtotal || 0)}
                </span>
              </div>
            </div>
            
            {invoice.tax !== undefined && invoice.tax > 0 && (
              <div className="flex justify-between">
                <span className="text-sm text-gray-600 dark:text-gray-400">Tax</span>
                <span className="text-sm font-medium text-gray-900 dark:text-white">
                  {formatAmount(invoice.tax)}
                </span>
              </div>
            )}
            
            <div className="border-t border-gray-200 dark:border-gray-700 pt-3">
              <div className="flex justify-between">
                <span className="text-base font-semibold text-gray-900 dark:text-white">Grand Total</span>
                <span className="text-base font-semibold text-gray-900 dark:text-white">
                  {formatAmount(invoice.grandTotal || 0)}
                </span>
              </div>
            </div>
          </div>

          {/* Payment Information */}
          {invoice.paymentMethod && (
            <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
              <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-3">Payment Information</h3>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600 dark:text-gray-400">Payment Method</span>
                  <span className="text-sm font-medium text-gray-900 dark:text-white">
                    {invoice.paymentMethod}
                  </span>
                </div>
                {invoice.paymentReference && (
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600 dark:text-gray-400">Payment Reference</span>
                    <span className="text-sm font-medium text-gray-900 dark:text-white">
                      {invoice.paymentReference}
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Notes */}
          {invoice.notes && (
            <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
              <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-2">Notes</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">{invoice.notes}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default InvoiceDetail;